import React, { useCallback, useEffect, useState } from "react";
import { Newspaper, ExternalLink, MessageSquarePlus, RefreshCw, ChevronDown, Loader2, AlertCircle, Check } from "lucide-react";
import { AuthExpiredError, TurfTalk } from "@/api/client";
import { Skeleton } from "@/components/ui-kit";
import { statusLabel, publishedLabel } from "@/lib/news";
import { loadGameNews, modeSubnote } from "@/lib/gameNews";

const STATUS_STYLE = {
  live: "text-[#4ea152] border-[#4ea152]/40",
  cached: "text-[#F4B740] border-[#F4B740]/40",
  unavailable: "text-white/40 border-white/15",
};

function NewsRow({ item, onDiscuss, discussing, shared }) {
  const busy = discussing === item.id;
  const isShared = shared && shared.has(item.id);
  return (
    <div className="px-3 py-2.5 border-b border-[#2A3544] last:border-0" data-testid={`news-item-${item.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-white hover:text-[#7ec4e8] leading-snug inline-flex items-start gap-1 group"
              data-testid={`news-link-${item.id}`}
            >
              <span className="break-words">{item.title}</span>
              <ExternalLink size={12} className="mt-0.5 shrink-0 text-white/30 group-hover:text-[#7ec4e8]" />
            </a>
          ) : (
            <span className="text-sm text-white leading-snug break-words">{item.title}</span>
          )}
          {item.summary && <p className="text-[12px] text-white/50 mt-0.5 leading-snug line-clamp-2">{item.summary}</p>}
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-white/40" data-testid={`news-attrib-${item.id}`}>
            <span className="truncate max-w-[55%]">{item.source}</span>
            <span aria-hidden>·</span>
            <span className="tabnums">{publishedLabel(item.publishedAt)}</span>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDiscuss(item)}
        disabled={busy || isShared || !!discussing}
        data-testid={`news-discuss-${item.id}`}
        aria-label={isShared ? "Shared to the room" : `Discuss: ${item.title}`}
        className="mt-1.5 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium border border-[#2F7D32] text-[#4ea152] hover:bg-[#2F7D32]/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : isShared ? <Check size={12} /> : <MessageSquarePlus size={12} />}
        {isShared ? "Shared" : "Discuss"}
      </button>
    </div>
  );
}

// Compact news panel beside room chat (collapsible on narrow screens). It is
// fully independent of chat: a news outage/empty/error never blocks chat.
export function GameNews({ gameId = null, league = null, team = null, onDiscuss, discussing, shared, height = 560 }) {
  const [feed, setFeed] = useState(null); // normalized or null (loading)
  const [state, setState] = useState("loading"); // loading | ready | error
  const [open, setOpen] = useState(true);

  const load = useCallback(() => {
    setState("loading");
    loadGameNews(TurfTalk.news, { gameId, league, team, limit: 12 })
      .then((r) => { setFeed(r); setState("ready"); })
      .catch((e) => { if (!(e instanceof AuthExpiredError)) { setFeed(null); setState("error"); } });
  }, [gameId, league, team]);

  useEffect(() => { load(); }, [load]);

  const status = feed?.status || "unavailable";
  const items = feed?.items || [];
  const subnote = feed ? modeSubnote(feed.mode) : null;

  return (
    <div className="tt-panel rounded-xl flex flex-col lg:h-[var(--news-h)]" style={{ "--news-h": `${height}px` }} data-testid="game-news">
      <div className="px-3 h-12 flex items-center gap-2 border-b border-[#2A3544] shrink-0">
        <Newspaper size={15} className="text-white/60" />
        <span className="font-display tracking-wide text-white text-sm">Game News</span>
        {state === "ready" && (
          <span className={`ml-1 text-[10px] px-1.5 py-px rounded border ${STATUS_STYLE[status] || STATUS_STYLE.unavailable}`} data-testid="news-status">
            {statusLabel(status)}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={load} aria-label="Refresh news" data-testid="news-refresh" className="w-7 h-7 grid place-items-center rounded-md text-white/50 hover:text-white hover:bg-white/5 transition-colors">
            <RefreshCw size={13} />
          </button>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="game-news-body" data-testid="news-toggle" className="w-7 h-7 grid place-items-center rounded-md text-white/50 hover:text-white hover:bg-white/5 transition-colors lg:hidden">
            <ChevronDown size={15} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
          </button>
        </div>
      </div>

      <div id="game-news-body" className={`${open ? "flex" : "hidden"} lg:flex flex-col flex-1 min-h-0 overflow-y-auto tt-scroll`} aria-busy={state === "loading"}>
        {state === "loading" && <div className="p-3"><Skeleton className="h-20 mb-2" /><Skeleton className="h-20 mb-2" /><Skeleton className="h-20" /></div>}

        {state === "error" && (
          <div className="flex-1 grid place-items-center p-5 text-center" data-testid="news-error">
            <div>
              <AlertCircle className="mx-auto mb-2 text-white/40" size={20} />
              <p className="text-white/70 text-sm mb-3">News is unavailable right now.</p>
              <button onClick={load} className="px-3 py-1.5 rounded-lg tt-panel-3 text-white text-sm inline-flex items-center gap-2" data-testid="news-error-retry"><RefreshCw size={13} /> Retry</button>
            </div>
          </div>
        )}

        {state === "ready" && status === "unavailable" && (
          <div className="flex-1 grid place-items-center p-5 text-center" data-testid="news-unavailable">
            <div>
              <Newspaper className="mx-auto mb-2 text-white/30" size={20} />
              <p className="text-white/55 text-sm">News is unavailable right now.</p>
              <p className="text-white/35 text-[12px] mt-1">Chat is unaffected — carry on.</p>
            </div>
          </div>
        )}

        {state === "ready" && status !== "unavailable" && items.length === 0 && (
          <div className="flex-1 grid place-items-center p-5 text-center" data-testid="news-empty">
            <p className="text-white/50 text-sm">No recent news for this matchup yet.</p>
          </div>
        )}

        {state === "ready" && items.length > 0 && (
          <div data-testid="news-list">
            {subnote && <p className="px-3 pt-2 text-[11px] text-white/45" data-testid="news-subnote">{subnote}</p>}
            {items.map((it) => <NewsRow key={it.id} item={it} onDiscuss={onDiscuss} discussing={discussing} shared={shared} />)}
          </div>
        )}
      </div>
    </div>
  );
}
